import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { View, ViewProps, Platform, Text } from "react-native";
import { Image } from "expo-image";
import {
  MediaContentData,
  ScreenMediaContent,
} from "../providers/ConfigProvider";
import { YoutubeVideo } from "./YoutubeVideo";
import { Color } from "../../types";
import { isEqual, uniq } from "lodash";
import { useTranslation } from "react-i18next";
import { useTheme } from "../providers/ThemeProvider";
import videojs from "video.js";
import type VideoJsPlayer from "video.js/dist/types/player";
import "video.js/dist/video-js.css";
import { useStalledCheck } from "../hooks/useStalledCheck";

const mediaNotFoundTimeout = 5; // seconds

type InlineImageProps = {
  source?: string;
  duration: number;
  loadNextItem: () => void;
};

const InlineImage = (props: InlineImageProps) => {
  const [state, setState] = useState<InlineImageProps>(props);

  // Only update the state if the props have changed value
  useEffect(() => {
    const propsValues = {
      source: props.source,
      duration: props.duration,
    };
    const stateValues = {
      source: state.source,
      duration: state.duration,
    };
    if (!isEqual(propsValues, stateValues)) {
      setState(props);
    }
  }, [props, state]);

  // Set the timeout to load the next item once duration is reached
  useEffect(() => {
    const timeout = setTimeout(
      state.loadNextItem,
      (state.source == null ? mediaNotFoundTimeout : state.duration) * 1000,
    );

    return () => clearTimeout(timeout);
  }, [state]);

  if (state.source == null) {
    return <MediaNotFound />;
  }

  return (
    <Image
      contentFit="contain"
      cachePolicy="memory-disk"
      source={state.source}
      style={{
        flex: 1,
        width: "100%",
        height: "100%",
      }}
    />
  );
};

const InlineVideo = ({
  source,
  loadNextItem,
  loop,
  backgroundColor,
}: {
  source?: string;
  loadNextItem: () => void;
  loop: boolean;
  backgroundColor: Color;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<VideoJsPlayer | null>(null);
  const supported = Platform.OS === "web" && source != null;

  useEffect(() => {
    if (!supported) {
      const timeout = setTimeout(loadNextItem, mediaNotFoundTimeout * 1000);
      return () => clearTimeout(timeout);
    }

    if (videoRef.current == null) {
      return;
    }

    if (playerRef.current == null) {
      playerRef.current = videojs(videoRef.current, {
        controls: true,
        autoplay: true,
      });
    }

    // Handle video ended event
    playerRef.current.on("ended", () => {
      loadNextItem();

      if (loop) {
        playerRef.current?.currentTime(0);
        playerRef.current?.play();
      }
    });
  }, [supported, loadNextItem, loop]);

  useStalledCheck(
    () => Promise.resolve(playerRef.current?.currentTime() ?? 0),
    loadNextItem,
  );

  if (!supported) {
    return <MediaNotFound />;
  }

  return (
    <View style={{ flex: 1 }}>
      <video
        ref={videoRef}
        className="video-js"
        style={{
          width: "100%",
          height: "100%",
          background: backgroundColor,
        }}
      >
        <source src={source} type="video/mp4" />
      </video>
    </View>
  );
};

const MediaNotFound = () => {
  const theme = useTheme();
  const { t } = useTranslation("MediaContentCarosel");

  return (
    <View
      style={{
        flex: 1,
        width: "100%",
        height: "100%",
        backgroundColor: theme.background,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Text
        style={{ color: theme.backgroundText, fontSize: theme.fontSize.large }}
      >
        {t("Media not found")}
      </Text>
    </View>
  );
};

const EmptyState = ({ backgroundColor }: { backgroundColor?: Color }) => {
  const theme = useTheme();
  return (
    <View
      style={{
        flex: 1,
        backgroundColor: backgroundColor ?? theme.colors.black,
      }}
    />
  );
};

export const MediaContentCarosel = ({
  backgroundColor,
  onLoopComplete,
  ...props
}: ViewProps & {
  backgroundColor?: Color;
  onLoopComplete: () => void;
  data: MediaContentData;
}) => {
  const [activeIndex, setActiveIndex] = useState<{
    index: number;
    state: "loading" | "ready";
  }>({ index: 0, state: "ready" });
  const [unsupportedMedia, setUnsupoortedMedia] = useState<
    ScreenMediaContent[]
  >([]);
  const theme = useTheme();

  const data = useMemo(() => {
    return (props.data ?? []).filter((c) => {
      // ❌ youtube (webviews) not supported on ios
      if (Platform.OS === "ios" && c.type === "youtube") return false;
      if (unsupportedMedia.includes(c)) return false;
      return true;
    });
  }, [props.data, unsupportedMedia]);

  const loadNextItem = useCallback(() => {
    const next = activeIndex.index + 1;
    if (next < data.length) {
      setActiveIndex({ index: next, state: "loading" });
      return;
    }

    if (activeIndex.index !== 0) {
      setActiveIndex({ index: 0, state: "loading" });
    }

    onLoopComplete();
  }, [activeIndex.index, data.length, onLoopComplete]);

  useEffect(() => {
    if (activeIndex.state === "loading") {
      setActiveIndex((prev) => ({ ...prev, state: "ready" }));
      return;
    }

    if (activeIndex.index > 0 && activeIndex.index >= data.length) {
      setActiveIndex({ index: 0, state: "loading" });
    }
  }, [data, activeIndex]);

  const activeItem = data[activeIndex.index];

  if (activeIndex.state === "loading" || activeItem == null) {
    return <EmptyState backgroundColor={backgroundColor} />;
  }

  if (activeItem.type === "image") {
    return (
      <InlineImage
        source={activeItem.url}
        duration={activeItem.duration}
        loadNextItem={loadNextItem}
      />
    );
  }

  if (activeItem.type === "video") {
    return (
      <InlineVideo
        source={activeItem.url}
        loadNextItem={loadNextItem}
        loop={data.length === 1}
        backgroundColor={backgroundColor ?? theme.colors.black}
      />
    );
  }

  if (activeItem.type === "youtube") {
    return (
      <YoutubeVideo
        videoId={activeItem.youtubeId}
        loadNextItem={loadNextItem}
        loop={data.length === 1}
        onPlaybackFailed={() => {
          setUnsupoortedMedia((m) => uniq([...m, activeItem]));
        }}
      />
    );
  }

  throw new Error(`Unsupported media type: ${JSON.stringify(activeItem)}`);
};
